import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type { MailRedirectSettings, MailRedirectSettingsUpdate } from '@/types/api';

/**
 * Hooks für den UI-steuerbaren Mail-Testmodus (DEV-Redirect, S8-06 Paket A).
 *
 * GET/PUT /mail/settings verwalten den app_settings-Eintrag 'mail_redirect' -
 * er gewinnt IMMER über die ENV MAIL_REDIRECT_ALL_TO. Der Query-Key liegt
 * lokal hier (lib/query.ts gehört einem anderen Arbeitspaket); er folgt dem
 * Muster von queryKeys.mail.*.
 */

export const mailSettingsQueryKey = ['mail', 'settings'] as const;

export function useMailSettings(): ReturnType<typeof useQuery<MailRedirectSettings>> {
  return useQuery<MailRedirectSettings>({
    queryKey: mailSettingsQueryKey,
    queryFn: () => api.get<MailRedirectSettings>('/mail/settings'),
  });
}

export function useSetMailSettings(): ReturnType<
  typeof useMutation<MailRedirectSettings, Error, MailRedirectSettingsUpdate>
> {
  const qc = useQueryClient();
  return useMutation<MailRedirectSettings, Error, MailRedirectSettingsUpdate>({
    mutationFn: (body) =>
      api.put<MailRedirectSettings, MailRedirectSettingsUpdate>('/mail/settings', body),
    onSuccess: (data) => {
      qc.setQueryData(mailSettingsQueryKey, data);
      // /mail/config spiegelt den wirksamen Redirect (redirect_all_to) -
      // nach dem Umschalten neu laden.
      void qc.invalidateQueries({ queryKey: queryKeys.mail.config });
    },
  });
}

/**
 * true, wenn der Testmodus wirksam ist (Mails gehen NUR an die
 * Redirect-Adresse). Für Banner außerhalb der Einstellungsseite.
 */
export function isTestModeActive(
  settings: MailRedirectSettings | undefined
): settings is MailRedirectSettings & { redirect_address: string } {
  return settings != null && settings.redirect_enabled && settings.redirect_address != null;
}
